import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../UserContext';
import './ProjectManager.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup, faPlus, faPlusCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import ProjectTile from './ProjectTile/ProjectTile';
import ProjectDetails from './ProjectDetails/ProjectDetails';
import UploadManager from './UploadManager/UploadManager';
import OmicsViewModal from '../SampleManager/OmicsView/OmicsView';
import GlobalSearchModal from './GlobalSearchModal';

function ProjectManager() {
    const { user } = useUser();
    const [projects, setProjects] = useState([]);
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddProject, setShowAddProject] = useState(false);
    const [newProjectName, setNewProjectName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [showOmicsModal, setShowOmicsModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showSearchModal, setShowSearchModal] = useState(false);
    const searchRef = useRef(null);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const response = await fetch('https://service9.szapfs.org/parent-projects');
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            // Ensure each project has a reports array
            const projectsWithReports = data.map(project => ({
                ...project,
                reports: project.reports || []
            })).sort((a, b) => a.id - b.id);
            
            // Filter projects based on user role
            let filteredProjects = projectsWithReports;
            if (user?.role === 'Reviewer') {
                filteredProjects = projectsWithReports.filter(project => 
                    project.id === 1 || project.id === 14 || project.id === 5
                );
            }
            
            setProjects(filteredProjects);
            setError(null);
        } catch (err) {
            console.error('Error fetching projects:', err);
            setError('Failed to load projects. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    const handleProjectClick = (projectId) => {
        setSelectedProjectId(projectId);
    };

    const handleBackClick = () => {
        setSelectedProjectId(null);
    };

    const handleAddProject = async () => {
        if (!newProjectName.trim()) return;

        try {
            setIsCreating(true);
            const response = await fetch('https://service9.szapfs.org/project', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ name: newProjectName.trim() })
            });

            if (!response.ok) throw new Error('Failed to create project');

            // Refetch all projects to get fresh data
            await fetchProjects();
            setShowAddProject(false);
            setNewProjectName('');
        } catch (error) {
            console.error('Error creating project:', error);
            alert('Failed to create project. Please try again.');
        } finally {
            setIsCreating(false);
        }
    };

    const openOmicsView = () => {
        setShowOmicsModal(true);
    };

    if (loading) {
        return (
            <div className="PMA-container">
                <div className="PMAP-container">
                    <div className="PMAP-loading">
                        

                        
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="PMA-container">
                <div className="PMAP-container">
                    <div className="PMAP-error">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="PMA-container">
            <div className="PMAP-container">
                {selectedProjectId ? (
                    <ProjectDetails 
                        projectId={selectedProjectId} 
                        projectData={projects.find(p => p.id === selectedProjectId)}
                        onBackClick={handleBackClick} 
                    />
                ) : (
                    <>
                        <div className="PMAP-container-U">
                            <div className="PMAP-container-UL">
                            <FontAwesomeIcon 
                                icon={faLayerGroup}
                                className="UPM-upload-button"
                                onClick={openOmicsView}

                            />
                                <div className="PMP-search-container" ref={searchRef}>
                                    <input 
                                        className='PMP-search-General' 
                                        type="text" 
                                        placeholder="Search projects, subprojects, patients..." 
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setShowSearchModal(e.target.value.length > 0);
                                        }}
                                    />
                                    {searchTerm && (
                                        <button 
                                            className="PMP-search-clear" 
                                            onClick={() => {
                                                setSearchTerm('');
                                                setShowSearchModal(false);
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    )}
                                    {showSearchModal && (
                                        <GlobalSearchModal 
                                            searchTerm={searchTerm}
                                            isOpen={showSearchModal}
                                            onClose={() => setShowSearchModal(false)}
                                            onProjectSelect={(projectId) => {
                                                // Navigate to the selected project
                                                handleProjectClick(projectId);
                                            }}
                                            onSubprojectSelect={({ parentProjectId, subprojectId }) => {
                                                // First navigate to the parent project
                                                handleProjectClick(parentProjectId);
                                                
                                                // The ProjectDetails component will load, and its useEffect will run
                                                // which will load the subprojects. We need a small delay to ensure
                                                // the subprojects are loaded before trying to select one.
                                                setTimeout(() => {
                                                    // Find the ProjectDetails component and call its handleSubprojectClick
                                                    // This is a bit of a hack, but it's the simplest way to trigger the subproject selection
                                                    const projectDetails = document.querySelector('.PMP-container');
                                                    if (projectDetails) {
                                                        // Find the subproject with the matching ID
                                                        const subprojectElement = Array.from(
                                                            document.querySelectorAll('.PMP-subproject')
                                                        ).find(el => el.textContent.includes(subprojectId));
                                                        
                                                        if (subprojectElement) {
                                                            // Trigger a click on the subproject element
                                                            subprojectElement.click();
                                                        }
                                                    }
                                                }, 500); // 500ms delay should be enough for subprojects to load
                                            }}
                                        />
                                    )}
                                </div>
                            <FontAwesomeIcon 
                                icon={faPlusCircle}
                                className="PMP-add-project"
                                onClick={() => setShowAddProject(true)}

                            />
                            </div>
                
                        </div>
                        <div className="PMAP-container-D">
                            <div className="PMAP-grid">
                                {showAddProject && (
                                    <div className="PMAP-project PMAP-temp-project">
                                        <input
                                            type="text"
                                            className="PMAP-project-name-input"
                                            placeholder="Project Name"
                                            value={newProjectName}
                                            onChange={(e) => setNewProjectName(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && handleAddProject()}
                                            autoFocus
                                        />
                                        <div className="PMAP-temp-project-buttons">
                                            <button 
                                                className="PMAP-button"
                                                onClick={handleAddProject}
                                                disabled={isCreating || !newProjectName.trim()}
                                            >
                                                {isCreating ? 'Creating...' : 'Create'}
                                            </button>
                                            <button 
                                                className="PMAP-cancel-button"
                                                onClick={() => {
                                                    setShowAddProject(false);
                                                    setNewProjectName('');
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {projects.map((project) => (
                                    <ProjectTile 
                                        key={project.id} 
                                        project={project} 
                                        onProjectClick={() => handleProjectClick(project.id)}
                                    />
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
            {!selectedProjectId && (
                <div className="PMAPR-container">
                    <UploadManager />
                </div>
            )}
            
            {/* OmicsView Modal */}
            {showOmicsModal && (
                <OmicsViewModal 
                    isOpen={showOmicsModal}
                    onClose={() => setShowOmicsModal(false)}
                />
            )}
        </div>
    );
}

export default ProjectManager;